import { CommonModule } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { DxAutocompleteModule, DxButtonModule, DxSelectBoxModule, DxTextBoxModule } from "devextreme-angular";
import { ItemClickEvent, SelectionChangedEvent } from "devextreme/ui/autocomplete";
import dxTextBox from "devextreme/ui/text_box";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { SharedPipesModule } from "src/app/pipes/shared.pipes.module";
import { TextHighlightPipe } from "src/app/pipes/TextHighlight.filter";

interface SearchMatch {
  id: string;
  index: number;
  text: string;
  context: string;
}

@Component({
    selector: 'app-search',
    templateUrl: './app-html-search.component.html',
    styleUrls: ['./app-html-search.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, DxSelectBoxModule, DxTextBoxModule, DxTextBoxModule, DxButtonModule, SharedPipesModule]
})
export class SearchComponent implements OnInit, OnDestroy {
  @Input() content: any; 
  @Output() searchValueChanged = new EventEmitter<string>();
  searchText: string = '';
  matches: SearchMatch[] = [];
  matchIndex: number | null = null;

  highlightedContent: string = ''; // Store the original HTML content
  private contextLength: number = 30; // Number of characters to show before and after match
  private originalContentHtml = null;

  searchVisible: boolean = false;

  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription;

  ngOnInit() {
    this.highlightedContent = this.content.innerHTML;
    this.searchSubscription = this.searchSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.findMatches();
    });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  onSearchChange($event) {
    if (this.originalContentHtml == null) {
      this.originalContentHtml = this.content.innerHTML;
    }
    if (!this.searchText || this.searchText.length < 1) {
      // If no search text, reset content to original HTML and clear matches immediately
      this.matches = [];
      this.content.innerHTML = this.originalContentHtml;
      this.searchValueChanged.emit($event.value);
      this.matchIndex = null;
      return;
    }
    this.searchSubject.next(this.searchText);
    this.searchValueChanged.emit($event.value);
  }

  private findMatches() {
    // Parse the original HTML content to create a DOM tree
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.originalContentHtml, 'text/html');
    const contentElement = doc.body;
    
    // Get the text content without modifying the DOM structure
    const contentText = this.content.innerText;
    const searchRegex = new RegExp(this.escapeRegExp(this.searchText), 'gi');
    this.matches = [];
    let match;

    // Find all matches in the text content
    while ((match = searchRegex.exec(contentText)) !== null) {
      // Get surrounding context
      const contextStart = Math.max(0, match.index - this.contextLength);
      const contextEnd = Math.min(contentText.length, match.index + match[0].length + this.contextLength);
      const context = contentText.substring(contextStart, contextEnd);

      this.matches.push({
        index: this.matches.length,
        text: match[0],
        context: context,
        id: `match-${this.matches.length}`,
      });
    }

    // If there are no matches, just return the original content
    if (this.matches.length === 0) {
      this.content.innerHTML = this.originalContentHtml;
      this.matchIndex = null;
      
      return;
    }

    // Create a TreeWalker to iterate through text nodes only
    const walker = document.createTreeWalker(
      contentElement,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function(node) {
          // Skip text nodes that are within images, scripts, style, or already highlighted
          const parent = node.parentNode;
          if (parent instanceof Element) {
            if (parent.nodeName === 'IMG' || 
                parent.nodeName === 'SCRIPT' || 
                parent.nodeName === 'STYLE' ||
                parent.classList.contains('highlight')) {
              return NodeFilter.FILTER_REJECT;
            }
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    // Collect all valid text nodes
    const textNodes: Node[] = [];
    let currentNode: Node | null;
    while ((currentNode = walker.nextNode()) !== null) {
      textNodes.push(currentNode);
    }

    // Process text nodes to add highlighting
    let matchCount = 0;
    for (const textNode of textNodes) {
      const text = textNode.nodeValue;
      if (!text) continue;

      const fragments = [];
      let lastIndex = 0;
      searchRegex.lastIndex = 0; // Reset regex index for each node

      while ((match = searchRegex.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          fragments.push(document.createTextNode(text.substring(lastIndex, match.index)));
        }

        // Create highlighted span for the match
        const highlightSpan = document.createElement('span');
        highlightSpan.className = 'highlight';
        highlightSpan.id = `match-${matchCount}`;
        highlightSpan.textContent = match[0];
        fragments.push(highlightSpan);

        lastIndex = match.index + match[0].length;
        matchCount++;
      }

      // Add remaining text after the last match
      if (lastIndex < text.length) {
        fragments.push(document.createTextNode(text.substring(lastIndex)));
      }

      // If we have fragments (meaning we found matches), replace the text node
      if (fragments.length > 0) {
        const parentNode = textNode.parentNode;
        if (parentNode) {
          // Replace the text node with our fragments
          fragments.forEach(fragment => {
            parentNode.insertBefore(fragment, textNode);
          });
          parentNode.removeChild(textNode);
        }
      }
    }

    // Update the content with the modified DOM structure
    this.content.innerHTML = contentElement.innerHTML;

    // Ensure the selected match index is valid
    if (this.matches.length > 0) {
      
      this.matchIndex = 0;
      this.onMatchChange();
    } else {
      
    }
  }

  onMatchSelect($e: ItemClickEvent) {
    this.matchIndex = $e.itemData.index;
    document.getElementById($e.itemData.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  onNextMatch() {
    if (this.matchIndex == null && this.matches.length > 0) {
      
      this.matchIndex = 0;
    } else {
      this.matchIndex++;
    }
    if (this.matches.length == 0) { this.matchIndex = null; }
    if (this.matchIndex >= this.matches.length && this.matches.length > 0) {
      
      this.matchIndex = 0;
    }

    this.onMatchChange();
  }

  onPrevMatch() {
    if (this.matchIndex == null && this.matches.length > 0) {
      this.matchIndex = this.matches.length - 1;
    } else {
      this.matchIndex--;
    }
    if (this.matches.length == 0) { this.matchIndex = null; }

    if (this.matchIndex < 0) {
      this.matchIndex = this.matches.length - 1;
    }
    this.onMatchChange();
  }

  onMatchChange() {
    if (this.matchIndex == null) { return; }
    if (this.matches.length == 0) { return; }

    let el = document.getElementById(this.matches[this.matchIndex].id);
    if (el == null) { return; }

    document.querySelector(".highlight.selected")?.classList.remove("selected");

    el.classList.add("selected");

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}